import { useEffect } from 'react';
import { useMarketingConfig } from '../../hooks/useMarketingSettings';

// Script IDs for cleanup
const SCRIPT_IDS = {
  gtm: 'oneform-gtm',
  ga4: 'oneform-ga4',
  ga4Inline: 'oneform-ga4-inline',
  metaPixel: 'oneform-meta-pixel',
};

function injectGTM(containerId: string) {
  if (document.getElementById(SCRIPT_IDS.gtm)) return;
  const script = document.createElement('script');
  script.id = SCRIPT_IDS.gtm;
  script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${sanitizeId(containerId)}');`;
  document.head.appendChild(script);
}

function injectGA4(measurementId: string) {
  if (document.getElementById(SCRIPT_IDS.ga4)) return;
  const script = document.createElement('script');
  script.id = SCRIPT_IDS.ga4;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${sanitizeId(measurementId)}`;
  document.head.appendChild(script);

  const inlineScript = document.createElement('script');
  inlineScript.id = SCRIPT_IDS.ga4Inline;
  inlineScript.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${sanitizeId(measurementId)}');`;
  document.head.appendChild(inlineScript);
}

function injectMetaPixel(pixelId: string) {
  if (document.getElementById(SCRIPT_IDS.metaPixel)) return;
  const script = document.createElement('script');
  script.id = SCRIPT_IDS.metaPixel;
  script.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${sanitizeId(pixelId)}');fbq('track','PageView');`;
  document.head.appendChild(script);
}

function removeScript(id: string) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

// Sanitize IDs before injecting into inline scripts (defense-in-depth against XSS)
function sanitizeId(id: string): string {
  return id.replace(/[^A-Za-z0-9\-_]/g, '');
}

export const TrackingInjector: React.FC = () => {
  const { config, loading } = useMarketingConfig();

  useEffect(() => {
    if (loading) return;

    // Don't inject tracking scripts on admin pages
    const isAdmin = window.location.pathname.startsWith('/admin');
    if (isAdmin) {
      // Clean up any previously injected scripts
      Object.values(SCRIPT_IDS).forEach(removeScript);
      return;
    }

    // GTM
    if (config.gtm.enabled && config.gtm.containerId) {
      injectGTM(config.gtm.containerId);
    } else {
      removeScript(SCRIPT_IDS.gtm);
    }

    // GA4
    if (config.ga4.enabled && config.ga4.measurementId) {
      injectGA4(config.ga4.measurementId);
    } else {
      removeScript(SCRIPT_IDS.ga4);
      removeScript(SCRIPT_IDS.ga4Inline);
    }

    // Meta Pixel
    if (config.metaPixel.enabled && config.metaPixel.pixelId) {
      injectMetaPixel(config.metaPixel.pixelId);
    } else {
      removeScript(SCRIPT_IDS.metaPixel);
    }
  }, [config, loading]);

  return null;
};
